import { Client } from "@modelcontextprotocol/sdk/client/index.js";
import { StreamableHTTPClientTransport } from "@modelcontextprotocol/sdk/client/streamableHttp.js";

import type { AgentConfig } from "./index";

// A real, live MCP client connected directly from the browser to the same dev proxy the hosted
// agent talks to (UnityAgent.Proxy). This lets an AppBridge (connectMcpApp) forward
// `app.callServerTool(...)` calls from an MCP UI App's iframe straight to the MCP server — the
// same "browser calls the MCP server directly for tool-call-backs" pattern UnityAgent.Proxy's
// CORS policy already grants to M365 Copilot's widget-renderer origin.

export function createMcpClient(agent: AgentConfig, getConversationKey: () => string | undefined) {
  let clientPromise: Promise<Client> | undefined;
  const resourcePromises = new Map<string, Promise<string>>();

  function getMcpResourceHtml(uri: string): Promise<string> {
    let promise = resourcePromises.get(uri);
    if (promise == null) {
      promise = (async () => {
        const client = await getMcpDirectClient();
        const result = await client.readResource({ uri });
        const resource = result.contents.find((item) => item.uri === uri && "text" in item);
        if (resource == null || !("text" in resource) || resource.mimeType?.startsWith("text/html") !== true) {
          throw new Error(`MCP resource '${uri}' did not return HTML content.`);
        }
        return resource.text;
      })().catch((error: unknown) => {
        resourcePromises.delete(uri);
        throw error;
      });
      resourcePromises.set(uri, promise);
    }
    return promise;
  }

  // Custom fetch so the tenant/session headers reflect the conversation key at call time (read
  // fresh per request), not whatever it was when the client was first constructed.
  function conversationAwareFetch(): typeof fetch {
    return (input, init) => {
      const headers = new Headers(init?.headers);
      headers.set("x-tenant-id", agent.tenantId);
      if (agent.environmentName) {
        headers.set("X-Unity-Environment-Id", agent.environmentName);
      }
      const conversationKey = getConversationKey();
      if (conversationKey) {
        headers.set("x-ms-client-session-id", conversationKey);
      }
      return fetch(input, { ...init, headers });
    };
  }

  /** Lazily connects (once) a direct MCP client to the same server the hosted agent uses. */
  function getMcpDirectClient(): Promise<Client> {
    if (!clientPromise) {
      clientPromise = (async () => {
        const client = new Client({ name: "unity-viewer", version: "1.0.0" });
        const transport = new StreamableHTTPClientTransport(new URL(agent.mcpServerUrl), {
          fetch: conversationAwareFetch(),
        });
        await client.connect(transport);
        return client;
      })().catch((err) => {
        // Don't cache a failed connection attempt (e.g. a transient 503 from the dev proxy) —
        // otherwise every widget for the rest of the page session would keep reusing this same
        // rejected promise and never render.
        clientPromise = undefined;
        throw err;
      });
    }
    return clientPromise;
  }

  return { getMcpDirectClient, getMcpResourceHtml };
}
